import { partition } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { CeilingPlane, RoofPlane } from '@paragon/contract-library';
import { Direction2D, Line2D, LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { centerPoint2D } from '@paragon/geometry-library/source/scripts/Polygon2D';
import {
  calculateBearingEnvelopeShape,
  calculateBoundedPlane,
  calculateFloorPolyline2DShape,
  calculateOverhangsAndSubfasciaeForBoundedPlane,
  calculateTrussEnvelopeShape,
  CeilingTrayGeometry,
  isRoofPlane,
} from '@paragon/layout';
import { getAllMembers } from '@paragon/relative-geometry';
import { calculateMemberPolygon, platePairToPolygonsWorldCoordinates } from '@paragon/three-shared';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { getActiveComponent } from '../../../store/selectors/getActiveComponentSelector';
import {
  getAllBearingEnvelopesDictionary,
  getVisibleBearingEnvelopes,
  getVisibleCeilingPlaneGuids,
  getVisibleCeilingTrays,
  getVisibleFeaturePatternContainers,
  getVisibleRoofPlaneGuids,
  getVisibleTrussEnvelopes,
  getVisibleWorkLines,
} from '../../../store/selectors/layoutDataSelectors';
import { getCalculatedRoofAndCeilingPlanes } from '../../../store/selectors/roofAndCeilingPlaneSelectors';
import { getActiveWebbingInfo } from '../../../store/selectors/webbingInfoSelectors';
import { calculateLayoutFeatureArea2DShape } from '../../layout-geometry/layoutFeatureAreas';
import { getSelectionWindowMode } from './modes';
import { createTargetCollectionForMode } from './types';

const WEB_SEGMENT_SHAPE_OFFSET = 0.25;

const getVisibleWorkLinesWithShapes = createSelector(getVisibleWorkLines, (workLines) =>
  workLines.map(
    (workLine) =>
      [
        workLine,
        'endPoint' in workLine.geometry
          ? LineSegment2D.fromLineSegmentObject(workLine.geometry)
          : Line2D.fromObject(workLine.geometry),
      ] as const,
  ),
);

const getVisibleTrussEnvelopesWithShapes = createSelector(getVisibleTrussEnvelopes, (trussEnvelopes) =>
  trussEnvelopes.map((envelope) => [envelope, calculateTrussEnvelopeShape(envelope)] as const),
);

const getVisibleBeamsWithShapes = createSelector(getVisibleBearingEnvelopes, (bearingEnvelopes) =>
  bearingEnvelopes
    .filter((bearing) => bearing.bearingType === 'Beam')
    .map((bearing) => [bearing, calculateBearingEnvelopeShape(bearing)] as const),
);

const getVisibleWallsWithShapes = createSelector(getVisibleBearingEnvelopes, (bearingEnvelopes) =>
  bearingEnvelopes
    .filter((bearing) => bearing.bearingType === 'Wall')
    .map((bearing) => [bearing, calculateBearingEnvelopeShape(bearing)] as const),
);

const getVisiblePlanesWithShapes = createSelector(
  getCalculatedRoofAndCeilingPlanes,

  getVisibleRoofPlaneGuids,
  getVisibleCeilingPlaneGuids,

  (calculatedPlanes, visibleRoofPlanes, visibleCeilingPlanes) => {
    const taggedPolygons = Object.entries(calculatedPlanes)
      .map(([guid, plane]) => {
        if (plane != null) {
          if (visibleRoofPlanes.has(guid)) return { plane, type: 'roof' } as const;
          if (visibleCeilingPlanes.has(guid)) return { plane, type: 'ceiling' } as const;
        }

        return null;
      })
      .filter(notNullish)
      .map(({ plane, type }) => {
        const bounds = calculateBoundedPlane(plane, calculatedPlanes);
        // un-bounded planes still appear in the scene and need to be selectable
        if (!bounds.shape.length) {
          return { plane, type, shape: plane.calculatedGeometry.segment ?? plane.calculatedGeometry.line };
        }

        // ceiling planes won't have overhangs or subfasciae regions
        if (!isRoofPlane(plane)) return { plane, type, shape: bounds.shape };

        const otherRegions = calculateOverhangsAndSubfasciaeForBoundedPlane(plane, bounds);
        const polygons = [...bounds.shape, ...(otherRegions.overhangs ?? []), ...(otherRegions.subfasciae ?? [])];
        return { plane, type, shape: polygons };
      })
      .filter(notNullish)
      .map(({ plane, type, shape }) => {
        if (shape instanceof Line2D || shape instanceof LineSegment2D) {
          // no processing needed as these are already 2D
          return { plane, type, shapes: [shape] as (Line2D | LineSegment2D)[] };
        } else {
          const twoDimensionalPolygons = shape.map((polygon) => polygon.projectTo2D()).filter(notNullish);
          if (twoDimensionalPolygons.length === 0) return null;

          return { plane, type, shapes: twoDimensionalPolygons };
        }
      })
      .filter(notNullish);

    const [roofPlaneTuples, ceilingPlaneTuples] = partition(taggedPolygons, (polygon) => polygon.type === 'roof');

    const roofPlaneTargets = roofPlaneTuples.map(({ plane, shapes }) => [plane as RoofPlane, shapes] as const);
    const ceilingPlaneTargets = ceilingPlaneTuples.map(({ plane, shapes }) => [plane as CeilingPlane, shapes] as const);

    return {
      roofPlane: roofPlaneTargets,
      ceilingPlane: ceilingPlaneTargets,
    };
  },
);

const getVisibleCeilingTraysWithShapes = createSelector(getVisibleCeilingTrays, (ceilingTrays) =>
  ceilingTrays.map(
    (tray) =>
      [
        tray,
        CeilingTrayGeometry.fromTray(tray)
          ?.faces?.map((face) => face.projectTo2D())
          .filter(notNullish) ?? null,
      ] as const,
  ),
);

const getVisibleFeatureAreasWithShapes = createSelector(
  getVisibleFeaturePatternContainers,
  (featurePatternContainers) =>
    featurePatternContainers
      .flatMap((container) => container.featureAreas)
      .map((area) => [area, calculateLayoutFeatureArea2DShape(area)] as const),
);

const getVisibleFeaturePatternContainersWithShapes = createSelector(
  getVisibleFeaturePatternContainers,
  getAllBearingEnvelopesDictionary,
  (featurePatternContainers, bearingEnvelopes) =>
    featurePatternContainers
      .map((container) => {
        const calculationResult = calculateFloorPolyline2DShape(container.shape, bearingEnvelopes);
        return calculationResult && ([container, calculationResult] as const);
      })
      .filter(notNullish),
);

const getActiveLayoutTargets = createSelector(
  [
    getVisibleWorkLinesWithShapes,
    getVisibleTrussEnvelopesWithShapes,
    getVisibleBeamsWithShapes,
    getVisibleWallsWithShapes,
    getVisibleFeatureAreasWithShapes,
    getVisibleFeaturePatternContainersWithShapes,
    getVisiblePlanesWithShapes,
    getVisibleCeilingTraysWithShapes,
  ],
  (
    workLine,
    trussEnvelope,
    beam,
    wall,
    featureArea,
    featurePatternContainer,
    { roofPlane, ceilingPlane },
    ceilingTray,
  ) =>
    createTargetCollectionForMode('layout', {
      beam,
      ceilingPlane,
      featureArea,
      featurePatternContainer,
      roofPlane,
      trussEnvelope,
      wall,
      workLine,
      ceilingTray,
    }),
);

const getVisibleMembersWithShapes = createSelector(getActiveComponent, (component) => {
  if (component == null) return [];

  return getAllMembers(component)
    .map((member) => [member, calculateMemberPolygon(member, component)!] as const)
    .filter(([_, polygon]) => polygon != null);
});

const getVisiblePlatePairsWithShapes = createSelector(getActiveComponent, (component) => {
  if (component == null) return [];

  return (
    component.platePairs
      // FIXME: calling `platePairToPolygon` on top-bottom plates produces a polygon that will project to a line
      //   segment. We need a method to calculate a top-down hitbox polygon for top-bottom plates before they can be
      //   included in this tool.
      .filter((platePair) => platePair.platePlacement !== 'TopBottom')
      .map((platePair) => [platePair, platePairToPolygonsWorldCoordinates(platePair)] as const)
      .filter(([_, polygons]) => polygons.every((polygon) => polygon.length > 2))
      .map(
        ([platePair, polygons]) =>
          [platePair, polygons.map((polygon) => new Polygon2D(polygon.map(Point2D.fromObject)))] as const,
      )
  );
});

const getActiveDesignTargets = createSelector(
  [getVisibleMembersWithShapes, getVisiblePlatePairsWithShapes],
  (member, platePair) =>
    createTargetCollectionForMode('design', {
      member,
      platePair,
    }),
);

const getVisibleWebSegmentsWithShapes = createSelector(getActiveWebbingInfo, (webbingInfo) => {
  if (!webbingInfo) return [];

  return webbingInfo.webbingRegions.flatMap((webbingRegion) =>
    webbingRegion.webSegments
      .map((webSegment) => {
        const baseJoint = webbingRegion.calculatedWebJoints.find((joint) => joint.guid === webSegment.baseJointGuid);
        if (!baseJoint) {
          console.error(`Failed to find calculated web joint with GUID ${webSegment.baseJointGuid}`);
          return;
        }

        const endJoint = webbingRegion.calculatedWebJoints.find((joint) => joint.guid === webSegment.endJointGuid);
        if (!endJoint) {
          console.error(`Failed to find calculated web joint with GUID ${webSegment.endJointGuid}`);
          return;
        }

        let basePoint = Point2D.fromObject(baseJoint.location);
        let endPoint = Point2D.fromObject(endJoint.location);
        if (basePoint.equals(endPoint)) {
          // Ideally this shouldn't happen, but if it does let's just offset the points from each other
          const centerPoint = centerPoint2D([basePoint, endPoint]);
          basePoint = centerPoint.translate(Direction2D.up.toVector(WEB_SEGMENT_SHAPE_OFFSET));
          endPoint = centerPoint.translate(Direction2D.down.toVector(WEB_SEGMENT_SHAPE_OFFSET));
        }

        const perpendicularDirection = Direction2D.fromPoints(basePoint, endPoint).rotateQuarterTurn();

        return [
          webSegment,
          new Polygon2D([
            basePoint.translate(perpendicularDirection.toVector(WEB_SEGMENT_SHAPE_OFFSET)),
            endPoint.translate(perpendicularDirection.toVector(WEB_SEGMENT_SHAPE_OFFSET)),
            endPoint.translate(perpendicularDirection.toVector(-WEB_SEGMENT_SHAPE_OFFSET)),
            basePoint.translate(perpendicularDirection.toVector(-WEB_SEGMENT_SHAPE_OFFSET)),
          ]),
        ] as const;
      })
      .filter(notNullish),
  );
});

const getActiveWebbingTargets = createSelector([getVisibleWebSegmentsWithShapes], (webSegment) =>
  createTargetCollectionForMode('webbing', { webSegment }),
);

export const getActiveBulkSelectionWindowToolTargets = createSelector(
  [getSelectionWindowMode, getActiveLayoutTargets, getActiveDesignTargets, getActiveWebbingTargets],
  (mode, layoutTargets, designTargets, webbingTargets) => {
    switch (mode) {
      case 'layout':
        return layoutTargets;

      case 'webbing':
        return webbingTargets;

      case 'design':
        return designTargets;

      default:
        assertExhaustiveSwitch(mode);
    }
  },
);
